import { Navigate, useLocation } from 'react-router-dom'
import { useAuthentication, AuthenticationStatus } from '@mysaiva/core/authentication'
import { LoadingPage } from '@mysaiva/core'
import { getAvailableProducts, SaivaProduct, type UserMeRead } from '../utils/productRedirect'
import { UnauthorizedProductScreen } from './UnauthorizedProductScreen'

interface LocationState {
  from?: {
    pathname: string
    search?: string
    hash?: string
  }
}

interface ProductAccessCheckProps {
  product: SaivaProduct
}

/**
 * Component that checks if user has access to a product
 * - If they have access: renders nothing (product app will load at this route)
 * - If they don't have access: shows UnauthorizedProductScreen
 * - If not authenticated: redirects to login with return URL preserved in state
 *
 * This is used for product route paths (/upt, /therapy, /wounds).
 */
export function ProductAccessCheck({ product }: ProductAccessCheckProps) {
  const { user, status } = useAuthentication()
  const location = useLocation()

  const isLoading = status === AuthenticationStatus.Loading

  // Show loading while checking authentication
  if (isLoading) {
    return <LoadingPage />
  }

  // Not authenticated - redirect to login with the current location stored in state
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location } as LocationState}
      />
    )
  }

  // Check if user has access to this product
  const availableProducts = getAvailableProducts(user as UserMeRead)
  const hasAccess = availableProducts.includes(product)

  // User doesn't have access - show error page
  if (!hasAccess) {
    return <UnauthorizedProductScreen />
  }

  // User has access - render nothing (product app will mount here)
  return null
}
